import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import translations from './translations';

type Locale = 'en' | 'ru' | 'de' | 'es' | 'fr';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

function detectBrowserLocale(): Locale {
  try {
    const lang = navigator.language?.split('-')[0] || 'en';
    const supported: Locale[] = ['en', 'ru', 'de', 'es', 'fr'];
    return supported.includes(lang as Locale) ? (lang as Locale) : 'en';
  } catch {
    return 'en';
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => {
    const stored = localStorage.getItem('locale') as Locale | null;
    return stored || detectBrowserLocale();
  });

  useEffect(() => {
    localStorage.setItem('locale', locale);
    document.documentElement.lang = locale;
  }, [locale]);

  function t(key: string): string {
    return translations[locale]?.[key] || translations.en?.[key] || key;
  }

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
